import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import BugReportIcon from '@mui/icons-material/BugReport';
import CheckIcon from '@mui/icons-material/Check';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReplyIcon from '@mui/icons-material/Reply';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import {
  Box,
  Button,
  IconButton,
  Link as MuiLink,
  MenuItem,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { TICKET_STATUSES, TICKET_TILES, TICKET_VIEWS } from '@app/shared';
import type { TicketGroupDto, TicketListItemDto, TicketTile, TicketView } from '@app/shared';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { usePageTitle } from '../../app/pageTitle';
import { tokens } from '../../app/theme';
import { StatusChip } from '../../components/StatusChip';
import type { Query } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { useAuth } from '../auth/AuthProvider';
import {
  ShowTicketDialog,
  TicketFormDialog,
  TicketGroupDialog,
  TicketGroupsTable,
  TicketTypeDialog,
} from './TicketDialogs';
import { ReleasedFeature } from '../../release';
import {
  ticketExportUrl,
  ticketReportUrl,
  useTicketCounts,
  useTicketTypeOptions,
  useTickets,
} from './api';

/** "Record Limit" options of the reference. */
const RECORD_LIMITS = [10, 20, 30, 50, 100, 120, 150, 200];

interface Filters {
  ticketNo: string;
  customerId: string;
  status: string;
  typeId: string;
  order: 'latest' | 'oldest';
}
const NO_FILTERS: Filters = {
  ticketNo: '',
  customerId: '',
  status: '',
  typeId: '',
  order: 'latest',
};

/** Icon and colour of each tile, as on the reference. */
const TILE_LOOK: Record<TicketTile, { color: string; icon: ReactNode }> = {
  total: { color: tokens.stat.aqua, icon: <ConfirmationNumberIcon fontSize="large" /> },
  callBack: { color: tokens.stat.fuchsia, icon: <ReplyIcon fontSize="large" /> },
  escalation: { color: tokens.stat.fuchsia, icon: <BugReportIcon fontSize="large" /> },
  opened: { color: tokens.stat.green, icon: <OpenInNewIcon fontSize="large" /> },
  closed: { color: tokens.stat.red, icon: <CheckIcon fontSize="large" /> },
  inProgress: { color: tokens.stat.blue, icon: <AutorenewIcon fontSize="large" /> },
  pending: { color: tokens.stat.blue, icon: <HourglassEmptyIcon fontSize="large" /> },
  mine: { color: tokens.stat.yellow, icon: <ThumbUpIcon fontSize="large" /> },
};

/**
 * Tickets (PC722), the reference's "Ticketing System": eight tiles, the view tabs, the
 * actions, one filter row with Search, the list, and dialogs for everything else.
 */
export function TicketsPage() {
  const { can } = useAuth();
  usePageTitle('Ticketing System');
  const [view, setView] = useState<TicketView>('all');
  const [showGroups, setShowGroups] = useState(false);
  const [draft, setDraft] = useState<Filters>(NO_FILTERS);
  const [applied, setApplied] = useState<Filters>(NO_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string>();
  const [showing, setShowing] = useState<string>();
  const [typesOpen, setTypesOpen] = useState(false);
  const [group, setGroup] = useState<{ group?: TicketGroupDto } | null>(null);

  const params: Query = { ...applied, view, page, pageSize };
  const tickets = useTickets(params);
  const counts = useTicketCounts();
  const types = useTicketTypeOptions();

  const search = (e: FormEvent) => {
    e.preventDefault();
    setApplied(draft);
    setPage(1);
  };
  const set = <K extends keyof Filters>(key: K, value: Filters[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const columns: Column<TicketListItemDto>[] = [
    {
      key: '#',
      header: '#',
      width: 80,
      nowrap: true,
      render: (t) => (
        <MuiLink component="button" type="button" onClick={() => setShowing(t.id)}>
          {t.ticketNo}
        </MuiLink>
      ),
    },
    {
      key: 'title',
      header: 'Title',
      render: (t) => (
        <Box sx={{ display: 'grid', gap: 0.25, gridTemplateColumns: { md: '2fr 1fr 1fr' } }}>
          <Box>
            <MuiLink
              component="button"
              type="button"
              onClick={() => setShowing(t.id)}
              aria-label={`Show Ticket ${t.ticketNo} : ${t.title}`}
              sx={{ textAlign: 'left', fontSize: 15 }}
            >
              {t.title}
            </MuiLink>
            <Box sx={{ fontSize: 12 }}>
              <strong>Update By:</strong> {t.updatedBy?.loginId ?? ''} on{' '}
              {formatDateTime(t.updatedAt)}
            </Box>
          </Box>
          <Box sx={{ fontSize: 12 }}>
            <strong>Customer Name:</strong>{' '}
            {t.customer.id && can('PC56') ? (
              <MuiLink component={Link} to={`/customers/${t.customer.id}`}>
                {t.customer.name} ({t.customer.customerId})
              </MuiLink>
            ) : (
              `${t.customer.name} (${t.customer.customerId})`
            )}
          </Box>
          <Box sx={{ fontSize: 12 }}>
            <div>
              <strong>Created By:</strong> {t.createdBy?.loginId ?? ''}
            </div>
            <div>
              <strong>Created Date:</strong> {formatDateTime(t.createdAt)}
            </div>
          </Box>
        </Box>
      ),
    },
    { key: 'type', header: 'Ticket Type', render: (t) => t.type },
    { key: 'assignedTo', header: 'Assigned To', render: (t) => t.assignedTo },
    { key: 'status', header: 'Status', render: (t) => <StatusChip status={t.status} /> },
    {
      key: 'action',
      header: 'Action',
      render: (t) => (
        <Tooltip title="Edit Ticket">
          <IconButton
            size="small"
            onClick={() => setEditing(t.id)}
            aria-label={`Edit Ticket ${t.ticketNo}`}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <Box
        component="section"
        aria-label="Ticket totals"
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          mb: 2,
        }}
      >
        {TICKET_TILES.map(([key, label]) => (
          <Paper key={key} variant="outlined" sx={{ display: 'flex', minHeight: 64 }}>
            <Box
              sx={{
                width: 64,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: TILE_LOOK[key].color,
                color: '#fff',
              }}
            >
              {TILE_LOOK[key].icon}
            </Box>
            <Box sx={{ px: 1.5, py: 0.75, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: 13 }}>
                {label}
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 700 }} aria-label={`${label} count`}>
                {counts.data ? counts.data[key] : '…'}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', mb: 1.5 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            aria-label="Ticket views"
            value={showGroups ? null : view}
            onChange={(_e, next: TicketView | null) => {
              if (!next) return;
              setShowGroups(false);
              setView(next);
              setPage(1);
            }}
            sx={{ flexWrap: 'wrap' }}
          >
            {TICKET_VIEWS.map(([key, label]) => (
              <ToggleButton key={key} value={key} sx={{ textTransform: 'none', px: 1.5 }}>
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 'auto' }}>
            <ReleasedFeature name="tickets-download-report">
              <Button
                component="a"
                href={ticketReportUrl()}
                download
                variant="contained"
                startIcon={<DownloadIcon />}
              >
                Download Report
              </Button>
            </ReleasedFeature>
            <Button
              component="a"
              href={ticketExportUrl({ ...applied, view })}
              download
              variant="contained"
              startIcon={<DownloadIcon />}
            >
              Download Tickets CSV
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAdding(true)}>
              Add Ticket
            </Button>
            <Button variant="contained" onClick={() => setTypesOpen(true)}>
              View Ticket Type
            </Button>
            <Button
              variant="contained"
              aria-pressed={showGroups}
              onClick={() => setShowGroups(true)}
            >
              View Groups
            </Button>
            <Button variant="contained" onClick={() => setGroup({})}>
              Add Groups
            </Button>
          </Box>
        </Box>

        {showGroups ? (
          <TicketGroupsTable onEdit={(g) => setGroup({ group: g })} />
        ) : (
          <>
            <Box
              component="form"
              onSubmit={search}
              aria-label="Ticket filters"
              sx={{
                mb: 1.5,
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                alignItems: 'start',
              }}
            >
              <TextField
                select
                label="Record Limit"
                value={String(pageSize)}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {RECORD_LIMITS.map((n) => (
                  <MenuItem key={n} value={String(n)}>
                    {n}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Ticket No"
                value={draft.ticketNo}
                onChange={(e) => set('ticketNo', e.target.value)}
              />
              <TextField
                label="Customer ID"
                value={draft.customerId}
                onChange={(e) => set('customerId', e.target.value.replace(/\D/g, ''))}
                slotProps={{ htmlInput: { maxLength: 11, inputMode: 'numeric' } }}
              />
              <TextField
                select
                label="Status"
                value={draft.status}
                onChange={(e) => set('status', e.target.value)}
              >
                <MenuItem value="">Select</MenuItem>
                {TICKET_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Ticket Type"
                value={draft.typeId}
                onChange={(e) => set('typeId', e.target.value)}
              >
                <MenuItem value="">Select</MenuItem>
                {(types.data ?? []).map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Sort By"
                value={draft.order}
                onChange={(e) => set('order', e.target.value as Filters['order'])}
              >
                <MenuItem value="latest">Latest</MenuItem>
                <MenuItem value="oldest">Oldest</MenuItem>
              </TextField>
              <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 37 }}>
                <Button type="submit" variant="contained">
                  Search
                </Button>
              </Box>
            </Box>

            <DataTable
              label="Tickets"
              columns={columns}
              rows={tickets.data?.data}
              rowKey={(t) => t.id}
              loading={tickets.isFetching}
              error={tickets.error}
              onRetry={() => void tickets.refetch()}
              page={page}
              pageSize={pageSize}
              total={tickets.data?.total ?? 0}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              pageSizeOptions={RECORD_LIMITS}
            />
          </>
        )}
      </Paper>

      <TicketFormDialog
        open={adding || Boolean(editing)}
        ticketId={editing}
        onClose={() => {
          setAdding(false);
          setEditing(undefined);
        }}
      />
      <ShowTicketDialog ticketId={showing} onClose={() => setShowing(undefined)} />
      <TicketTypeDialog open={typesOpen} onClose={() => setTypesOpen(false)} />
      <TicketGroupDialog
        open={Boolean(group)}
        group={group?.group}
        onClose={() => setGroup(null)}
      />
    </Box>
  );
}
